import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PremiumLock from '../components/PremiumLock';
import { Drum, Play, Square, Crown, Minus, Plus, Heart, Volume2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Noise buffer cache - generated once, reused for every hit
const noiseCache = { snare: null, hihat: null, rate: 0 };

const ensureNoiseCache = (ctx) => {
  if (noiseCache.rate === ctx.sampleRate) return noiseCache;
  // Snare noise (0.2s)
  const snare = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
  const sd = snare.getChannelData(0);
  for (let i = 0; i < snare.length; i++) sd[i] = Math.random() * 2 - 1;
  // HiHat noise (0.05s)
  const hh = ctx.createBuffer(1, ctx.sampleRate * 0.05, ctx.sampleRate);
  const hd = hh.getChannelData(0);
  for (let i = 0; i < hh.length; i++) hd[i] = Math.random() * 2 - 1;
  noiseCache.snare = snare;
  noiseCache.hihat = hh;
  noiseCache.rate = ctx.sampleRate;
  return noiseCache;
};

// Web Audio API drum sound generator - uses cached noise buffers
const createDrumSound = (audioContext, type, scheduledTime = null) => {
  const now = scheduledTime || audioContext.currentTime;
  const cache = ensureNoiseCache(audioContext);
  
  switch(type) {
    case 'kick': {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.setValueAtTime(150, now);
      osc.frequency.exponentialRampToValueAtTime(0.01, now + 0.5);
      gain.gain.setValueAtTime(1, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
      osc.start(now);
      osc.stop(now + 0.5);
      break;
    }
    case 'snare': {
      const osc = audioContext.createOscillator();
      const oscGain = audioContext.createGain();
      osc.type = 'triangle';
      osc.connect(oscGain);
      oscGain.connect(audioContext.destination);
      osc.frequency.setValueAtTime(100, now);
      oscGain.gain.setValueAtTime(0.7, now);
      oscGain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      osc.start(now);
      osc.stop(now + 0.1);
      // Noise - reuse cached buffer
      const noise = audioContext.createBufferSource();
      noise.buffer = cache.snare;
      const noiseGain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 1000;
      noise.connect(filter);
      filter.connect(noiseGain);
      noiseGain.connect(audioContext.destination);
      noiseGain.gain.setValueAtTime(0.5, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
      noise.start(now);
      break;
    }
    case 'hihat': {
      const noise = audioContext.createBufferSource();
      noise.buffer = cache.hihat;
      const filter = audioContext.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 7000;
      const gain = audioContext.createGain();
      noise.connect(filter);
      filter.connect(gain);
      gain.connect(audioContext.destination);
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
      noise.start(now);
      break;
    }
    case 'ride': {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.type = 'sine';
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.setValueAtTime(400, now);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
      osc.start(now);
      osc.stop(now + 0.4);
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.type = 'sine';
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.setValueAtTime(800, now);
      gain2.gain.setValueAtTime(0.1, now);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      osc2.start(now);
      osc2.stop(now + 0.3);
      break;
    }
    default:
      break;
  }
};

const GENRE_THEMES = {
  rock:    { gradient: 'from-red-500 to-orange-500',    text: 'text-red-300',    pill: 'bg-red-500/15 text-red-300 border-red-500/30' },
  metal:   { gradient: 'from-red-600 to-rose-600',      text: 'text-red-300',    pill: 'bg-red-500/15 text-red-300 border-red-500/30' },
  punk:    { gradient: 'from-rose-500 to-pink-500',     text: 'text-rose-300',   pill: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  pop:     { gradient: 'from-pink-500 to-fuchsia-500',  text: 'text-pink-300',   pill: 'bg-pink-500/15 text-pink-300 border-pink-500/30' },
  blues:   { gradient: 'from-indigo-500 to-blue-500',   text: 'text-indigo-300', pill: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' },
  jazz:    { gradient: 'from-purple-500 to-violet-500', text: 'text-purple-300', pill: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  funk:    { gradient: 'from-yellow-500 to-orange-500', text: 'text-yellow-300', pill: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  country: { gradient: 'from-green-500 to-emerald-500', text: 'text-emerald-300',pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  folk:    { gradient: 'from-amber to-yellow-500',      text: 'text-amber',      pill: 'bg-amber/15 text-amber border-amber/30' },
  disco:   { gradient: 'from-fuchsia-500 to-pink-500',  text: 'text-fuchsia-300',pill: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30' },
  hiphop:  { gradient: 'from-slate-500 to-zinc-600',    text: 'text-zinc-300',   pill: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' },
  reggae:  { gradient: 'from-green-500 to-lime-500',    text: 'text-lime-300',   pill: 'bg-lime-500/15 text-lime-300 border-lime-500/30' },
  latin:   { gradient: 'from-orange-500 to-yellow-500', text: 'text-orange-300', pill: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  ballad:  { gradient: 'from-cyan-500 to-blue-400',     text: 'text-cyan-300',   pill: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
};

const getGenreTheme = (g) => GENRE_THEMES[(g || '').toLowerCase()] || { gradient: 'from-amber to-orange-500', text: 'text-amber', pill: 'bg-amber/15 text-amber border-amber/30' };

const DrumsPage = () => {
  const { t, i18n } = useTranslation();
  const { isPremium, isAuthenticated } = useAuth();
  const [loops, setLoops] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedLoop, setSelectedLoop] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentBpm, setCurrentBpm] = useState(120);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [favorites, setFavorites] = useState([]);
  
  const audioContextRef = useRef(null);
  const timerIdRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const currentBeatIndexRef = useRef(0);
  const isPlayingRef = useRef(false);
  const gainNodeRef = useRef(null);
  const patternRef = useRef([]);
  const bpmRef = useRef(120);

  // Web Audio Scheduler Constants
  const LOOKAHEAD = 25.0; // ms
  const SCHEDULE_AHEAD_TIME = 0.1; // seconds

  useEffect(() => {
    fetchGenres();
    fetchLoops();
    if (isAuthenticated) fetchFavorites();
    return () => {
      if (timerIdRef.current) clearInterval(timerIdRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  useEffect(() => {
    fetchLoops();
  }, [selectedGenre, selectedDifficulty]);

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/drums/genres`);
      setGenres(response.data.genres || []);
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  const fetchLoops = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedGenre !== 'all') params.append('genre', selectedGenre);
      if (selectedDifficulty !== 'all') params.append('difficulty', selectedDifficulty);
      
      const response = await axios.get(`${API}/drums?${params.toString()}`);
      setLoops(response.data);
    } catch (error) {
      console.error('Error fetching drum loops:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites?item_type=drum_loop`);
      setFavorites(response.data.map(f => f.item_id));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const toggleFavorite = async (loopId) => {
    if (!isAuthenticated) return;
    try {
      if (favorites.includes(loopId)) {
        await axios.delete(`${API}/favorites/drum_loop/${loopId}`);
        setFavorites(prev => prev.filter(id => id !== loopId));
      } else {
        await axios.post(`${API}/favorites?item_type=drum_loop&item_id=${loopId}`);
        setFavorites(prev => [...prev, loopId]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const getName = (loop) => i18n.language === 'it' ? loop.name_it : loop.name;
  const getDesc = (loop) => i18n.language === 'it' ? loop.description_it : loop.description;

  // Schedule drum sound at specific time
  const scheduleDrumSound = useCallback((time, sound) => {
    if (!audioContextRef.current || !sound) return;
    createDrumSound(audioContextRef.current, sound, time);
  }, []);

  // Scheduler function - runs on interval, schedules notes ahead
  const scheduler = useCallback(() => {
    if (!audioContextRef.current || !isPlayingRef.current) return;
    
    const pattern = patternRef.current;
    if (!pattern.length) return;
    
    const bpm = bpmRef.current;
    const noteDuration = (60.0 / bpm) / 2; // eighth notes
    
    // Schedule all notes that fall within the schedule window
    while (nextNoteTimeRef.current < audioContextRef.current.currentTime + SCHEDULE_AHEAD_TIME) {
      const beatIndex = currentBeatIndexRef.current % pattern.length;
      const sound = pattern[beatIndex];
      
      if (sound && sound.trim()) {
        scheduleDrumSound(nextNoteTimeRef.current, sound.trim());
      }
      
      // Update visual beat
      setCurrentBeat(beatIndex);
      
      // Advance to next note
      currentBeatIndexRef.current = (currentBeatIndexRef.current + 1) % pattern.length;
      nextNoteTimeRef.current += noteDuration;
    }
  }, [scheduleDrumSound]);

  const startPlaying = useCallback((loop) => {
    // Initialize AudioContext
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    
    // Setup refs
    patternRef.current = loop.pattern.split(',');
    bpmRef.current = loop.bpm;
    isPlayingRef.current = true;
    currentBeatIndexRef.current = 0;
    nextNoteTimeRef.current = audioContextRef.current.currentTime;
    
    // Update state
    setSelectedLoop(loop);
    setCurrentBpm(loop.bpm);
    setIsPlaying(true);
    setCurrentBeat(0);
    
    // Start scheduler loop
    timerIdRef.current = setInterval(scheduler, LOOKAHEAD);
  }, [scheduler]);

  const stopPlaying = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    setSelectedLoop(null);
    setCurrentBeat(0);
    
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }
  }, []);

  const adjustTempo = useCallback((delta) => {
    if (!selectedLoop || !isPlaying) return;
    
    const newBpm = Math.max(40, Math.min(220, currentBpm + delta));
    setCurrentBpm(newBpm);
    bpmRef.current = newBpm;
    // Scheduler will automatically use new BPM on next iteration
  }, [selectedLoop, isPlaying, currentBpm]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-4 md:px-8 lg:px-12" data-testid="drums-page">
      <div className="max-w-7xl mx-auto">
        {/* Magazine header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-amber/10 border border-amber/30 text-amber text-xs font-bold uppercase tracking-widest">
            <Drum className="w-3 h-3" />
            {t('drums.tagline', { defaultValue: 'Drum tracks' })}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-3">
            {t('drums.title')}
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            {t('drums.subtitle', {
              defaultValue: 'Loop di batteria pronti per esercitarti sopra. Filtra per genere, tempo o difficoltà. Tutti i BPM regolabili in tempo reale.',
              count: loops.length,
            })}
          </p>
        </motion.div>

        {/* Now Playing */}
        {isPlaying && selectedLoop && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-amber/30 rounded-2xl p-6 mb-8"
          >
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-amber/20 flex items-center justify-center">
                  <Drum className="w-7 h-7 text-amber animate-pulse" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white text-lg">{getName(selectedLoop)}</h3>
                  <p className="text-zinc-400 text-sm">{selectedLoop.genre} • {selectedLoop.time_signature}</p>
                </div>
              </div>

              {/* Beat Indicators */}
              <div className="flex gap-2">
                {selectedLoop.pattern.split(',').map((_, i) => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full transition-all ${
                      i === currentBeat % selectedLoop.pattern.split(',').length
                        ? 'bg-amber scale-125 shadow-glow'
                        : 'bg-zinc-700'
                    }`}
                  />
                ))}
              </div>

              {/* Tempo Control */}
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => adjustTempo(-5)}
                  className="text-zinc-400 hover:text-white"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <div className="text-center min-w-[80px]">
                  <span className="font-mono text-3xl text-amber font-bold">{currentBpm}</span>
                  <span className="text-zinc-500 text-sm ml-1">BPM</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => adjustTempo(5)}
                  className="text-zinc-400 hover:text-white"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <Button
                onClick={stopPlaying}
                data-testid="stop-drums-btn"
                className="bg-red-500 hover:bg-red-600 text-white px-6"
              >
                <Square className="w-4 h-4 mr-2 fill-current" />
                {t('drums.stop')}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#121212] border border-white/5 rounded-2xl p-5 mb-8 space-y-4"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mr-1">
              {t('drums.genre')}
            </span>
            <button
              onClick={() => setSelectedGenre('all')}
              data-testid="genre-all"
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                selectedGenre === 'all'
                  ? 'bg-amber text-black'
                  : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
              }`}
            >
              {t('drums.allGenres')}
            </button>
            {genres.map(g => (
              <button
                key={g}
                onClick={() => setSelectedGenre(g)}
                data-testid={`genre-${g}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${
                  selectedGenre === g
                    ? 'bg-amber text-black'
                    : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mr-1">
              {t('drums.level')}
            </span>
            {[
              { v: 'all', label: t('drums.allLevels') },
              { v: 'beginner', label: t('levels.beginner') },
              { v: 'intermediate', label: t('levels.intermediate') },
              { v: 'advanced', label: t('levels.expert') },
            ].map(opt => (
              <button
                key={opt.v}
                onClick={() => setSelectedDifficulty(opt.v)}
                data-testid={`difficulty-${opt.v}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  selectedDifficulty === opt.v
                    ? 'bg-amber text-black'
                    : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Loops Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loops.map((loop, index) => {
              const theme = getGenreTheme(loop.genre);
              const isCurrent = selectedLoop?.id === loop.id && isPlaying;
              return (
                <motion.div
                  key={loop.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.03, 0.5) }}
                  className={`relative bg-[#121212] border rounded-2xl overflow-hidden transition-all ${
                    isCurrent
                      ? 'border-amber/60 shadow-glow'
                      : 'border-white/5 hover:border-amber/30'
                  } ${loop.locked ? 'opacity-60' : ''}`}
                  data-testid={`drum-loop-${loop.id}`}
                >
                  {loop.locked && <PremiumLock />}

                  {/* Genre color band */}
                  <div className={`h-1 bg-gradient-to-r ${theme.gradient}`} />

                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3 gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center shrink-0`}>
                        <Drum className="w-6 h-6 text-black" />
                      </div>
                      <div className="flex items-center gap-2">
                        {loop.is_premium && <Crown className="w-4 h-4 text-amber" />}
                        {isAuthenticated && !loop.locked && (
                          <button onClick={() => toggleFavorite(loop.id)} aria-label="Favorite">
                            <Heart
                              className={`w-4 h-4 ${favorites.includes(loop.id) ? 'fill-red-500 text-red-500' : 'text-zinc-500'}`}
                            />
                          </button>
                        )}
                      </div>
                    </div>

                    <h3 className="font-heading text-lg font-bold text-white mb-1 leading-tight">
                      {getName(loop)}
                    </h3>
                    <p className="text-zinc-500 text-sm mb-4 line-clamp-2 leading-relaxed min-h-[2.5em]">
                      {getDesc(loop)}
                    </p>

                    <div className="flex flex-wrap gap-1.5 mb-4">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider ${theme.pill}`}>
                        {loop.genre}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-zinc-300 font-mono font-bold">
                        {loop.time_signature}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider ${
                        loop.difficulty === 'beginner'
                          ? 'bg-green-500/15 text-green-300 border-green-500/30'
                          : loop.difficulty === 'intermediate'
                            ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30'
                            : 'bg-purple-500/15 text-purple-300 border-purple-500/30'
                      }`}>
                        {t(`levels.${loop.difficulty === 'advanced' ? 'expert' : loop.difficulty}`)}
                      </span>
                    </div>

                    <div className="flex items-end justify-between gap-3 pt-3 border-t border-white/5">
                      <div>
                        <div className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold">BPM</div>
                        <div className={`font-mono text-3xl font-black leading-none ${theme.text}`}>{loop.bpm}</div>
                      </div>

                      {!loop.locked && (
                        <Button
                          onClick={() => isCurrent ? stopPlaying() : startPlaying(loop)}
                          data-testid={`play-loop-${loop.id}`}
                          size="sm"
                          className={
                            isCurrent
                              ? 'bg-red-500 hover:bg-red-600 text-white rounded-full px-4'
                              : `bg-gradient-to-r ${theme.gradient} text-black font-bold rounded-full px-4`
                          }
                        >
                          {isCurrent ? (
                            <>
                              <Square className="w-3.5 h-3.5 mr-1 fill-current" />
                              {t('drums.stop')}
                            </>
                          ) : (
                            <>
                              <Play className="w-3.5 h-3.5 mr-1 fill-current" />
                              {t('drums.play')}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && loops.length === 0 && (
          <div className="text-center py-20">
            <Drum className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">{t('drums.noResults')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrumsPage;
